import { auth } from '@/components/utils/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { database } from '@shared/database'
import { calculateMeshCodes } from '@shared/calculateMeshCodes'

/** 同一ログイン検知内で signIn 位置保存が二重に走らないようにする */
let signInLocationSaveInFlightUid = null

export const state = () => ({
  userDisplayName: null,
  userAuthEmail: null,
  isAuthenticating: true,
  isLoggedIn: false,
  isSigningOut: false,
  completedSetup: false,
  uid: null,
  isSetTelephoneNumber: false,
  isSetLineId: false,
  subscriptionEnder: null,
  isAnonymous: null,
  updateWorksUnsubscribe: null,
  updateCardsUnsubscribe: null,
  updateIncompleteWorksUnsubscribe: null,
  updateCompletedWorksUnsubscribe: null,
  updateReviewingWorksUnsubscribe: null,
  updateCancelWaitOrdersUnsubscribe: null,
  updateSumUnsubscribe: null,
  updateHistoriesUnsubscribe: null,
  updateMessageRoomsUnsubscribe: null,
  updateJoinedMessageRoomsUnsubscribe: null,
  updateMessagesUnsubscribe: null,
  globalDate: null,
  histories: [],
  agentId: null,
  lastPosition: null,
  lastPositionUpdatedAt: null
})

export const mutations = {
  updateUid(state, value) {
    state.uid = value
  },
  updateUserDisplayName(state, value) {
    state.userDisplayName = value
  },
  updateIsAuthenticating(state, value) {
    state.isAuthenticating = value
  },
  updateIsLoggedIn(state, value) {
    state.isLoggedIn = value
  },
  updateIsSigningOut(state, value) {
    state.isSigningOut = value
  },
  updateIsSetTelephoneNumber(state, value) {
    state.isSetTelephoneNumber = value
  },
  updateIsSetLineId(state, value) {
    state.isSetLineId = value
  },
  updateAuthStatus(state, user) {
    const saveSignInCallback = (isSetTelephoneNumber, isSetLineId) => {
      state.isSetTelephoneNumber = isSetTelephoneNumber
      state.isSetLineId = isSetLineId
      state.completedSetup = true
    }

    if (user && !state.isLoggedIn) {
      getAgentId(state)
      // 画面表示は completedSetup に依存するため、サインイン周りは遅延させない。
      // 位置保存だけミューテーション完了後にバックグラウンドで1回だけ走らせる。
      void saveSignInTime(user, saveSignInCallback).catch((error) => {
        console.error(error)
        saveSignInCallback(false, false)
      })
      if (navigator.geolocation) {
        scheduleSignInLocationSaveOnce(user)
      }
    } else {
      if (!user && !state.isAuthenticating) {
        state.completedSetup = true
      }
    }
    const isLoggedIn = !!user
    const hasAuthEmail = !!user && !!user.email
    state.userDisplayName = isLoggedIn ? user.displayName : null
    state.uid = isLoggedIn ? user.uid : null
    state.userAuthEmail = hasAuthEmail ? user.email : null
    state.agentId = isLoggedIn ? state.agentId : null
    state.isAuthenticating = false
    state.isLoggedIn = isLoggedIn
    state.isSigningOut = false
    state.isAnonymous = user ? user.isAnonymous : null
  },
  resetState(state) {
    signInLocationSaveInFlightUid = null
    state.uid = null
    state.userDisplayName = null
    state.userAuthEmail = null
    state.agentId = null
    state.isLoggedIn = false
    state.isAuthenticating = true
    state.isSigningOut = false
    state.isAnonymous = null
    state.updateWorksUnsubscribe = null
    state.updateCardsUnsubscribe = null
    state.updateIncompleteWorksUnsubscribe = null
    state.updateCompletedWorksUnsubscribe = null
    state.updateReviewingWorksUnsubscribe = null
    state.updateMyApplicantsUnsubscribe = null
    state.updateCancelWaitOrdersUnsubscribe = null
    state.updateSumUnsubscribe = null
    state.updateMessageRoomsUnsubscribe = null
    state.updateJoinedMessageRoomsUnsubscribe = null
    state.updateMessagesUnsubscribe = null
    state.lastPosition = null
    state.lastPositionUpdatedAt = null
  },
  setLastPosition(state, { lat, lng }) {
    state.lastPosition = { lat, lng }
    state.lastPositionUpdatedAt = Date.now()
  },
  updateUserAuthEmail(state, value) {
    state.userAuthEmail = value
  },
  updateSubscriptionEnder(state, value) {
    state.subscriptionEnder = value
  },
  setUpdateWorksUnsubscribe(state, value) {
    state.updateWorksUnsubscribe = value
  },
  setUpdateCardsUnsubscribe(state, value) {
    state.updateCardsUnsubscribe = value
  },
  setUpdateIncompleteWorksUnsubscribe(state, value) {
    state.updateIncompleteWorksUnsubscribe = value
  },
  setUpdateCompletedWorksUnsubscribe(state, value) {
    state.updateCompletedWorksUnsubscribe = value
  },
  setUpdateReviewingWorksUnsubscribe(state, value) {
    state.updateReviewingWorksUnsubscribe = value
  },
  setUpdateMyApplicantsUnsubscribe(state, value) {
    state.updateMyApplicantsUnsubscribe = value
  },
  setUpdateCancelWaitOrdersUnsubscribe(state, value) {
    state.updateCancelWaitOrdersUnsubscribe = value
  },
  setUpdateSumUnsubscribe(state, value) {
    state.updateSumUnsubscribe = value
  },
  setUpdateHistoriesUnsubscribe(state, value) {
    state.updateHistoriesUnsubscribe = value
  },
  setUpdateMessageRoomsUnsubscribe(state, value) {
    state.updateMessageRoomsUnsubscribe = value
  },
  setUpdateJoinedMessageRoomsUnsubscribe(state, value) {
    state.updateJoinedMessageRoomsUnsubscribe = value
  },
  setUpdateMessagesUnsubscribe(state, value) {
    state.updateMessagesUnsubscribe = value
  },
  updateGlobalDate(state, value) {
    state.globalDate = value
  },
  setHistories(state, histories) {
    state.histories = histories
  },
  updateAgentId(state, value) {
    state.agentId = value
  }
}

export const actions = {
  setupAuthStateHandler({ commit, state }) {
    const shouldSetupAuthstate =
      !state.isLoggedIn && state.isAuthenticating && !state.isSigningOut
    if (shouldSetupAuthstate) {
      const ender = onAuthStateChanged(auth, (user) => {
        commit('updateAuthStatus', user)
      })

      commit('updateSubscriptionEnder', ender)
    }
  },
  endSubscription({ state, commit }) {
    if (state.subscriptionEnder) {
      state.subscriptionEnder()
      commit('updateSubscriptionEnder', null)
    }
  },
  async signOut({ state, commit, dispatch }) {
    commit('updateIsSigningOut', true)
    setIsRegistCompleted(false)
    try {
      dispatch('endSubscription')
      if (state.updateWorksUnsubscribe) state.updateWorksUnsubscribe()
      if (state.updateCardsUnsubscribe) state.updateCardsUnsubscribe()
      if (state.updateMyApplicantsUnsubscribe)
        state.updateMyApplicantsUnsubscribe()
      if (state.updateCancelWaitOrdersUnsubscribe)
        state.updateCancelWaitOrdersUnsubscribe()
      if (state.updateSumUnsubscribe) state.updateSumUnsubscribe()
      if (state.updateMessageRoomsUnsubscribe)
        state.updateMessageRoomsUnsubscribe()
      if (state.updateMessagesUnsubscribe) state.updateMessagesUnsubscribe()
      await auth.signOut()
      commit('resetState')
    } catch (err) {
      console.error('error signing out of firebase', err)
    } finally {
      commit('updateIsSigningOut', false)
    }
  },
  setUnsubscribeUpdateWorks({ commit }, value) {
    commit('setUpdateWorksUnsubscribe', value)
  },
  setUnsubscribeUpdateCards({ commit }, value) {
    commit('setUpdateCardsUnsubscribe', value)
  },

  setUnsubscribeUpdateIncompleteWorks({ commit }, value) {
    commit('setUpdateIncompleteWorksUnsubscribe', value)
  },
  setUnsubscribeUpdateCompletedWorks({ commit }, value) {
    commit('setUpdateCompletedWorksUnsubscribe', value)
  },
  setUnsubscribeUpdateReviewingWorks({ commit }, value) {
    commit('setUpdateReviewingWorksUnsubscribe', value)
  },
  setUnsubscribeUpdateMyApplicants({ commit }, value) {
    commit('setUpdateMyApplicantsUnsubscribe', value)
  },
  setUnsubscribeUpdateCancelWaitingOrders({ commit }, value) {
    commit('setUpdateCancelWaitOrdersUnsubscribe', value)
  },
  setUnsubscribeUpdateSum({ commit }, value) {
    commit('setUpdateSumUnsubscribe', value)
  },
  setUnsubscribeUpdateHistories({ commit }, value) {
    commit('setUpdateHistoriesUnsubscribe', value)
  },
  setUnsubscribeUpdateMessageRooms({ commit }, value) {
    commit('setUpdateMessageRoomsUnsubscribe', value)
  },
  setUnsubscribeUpdateJoinedMessageRooms({ commit }, value) {
    commit('setUpdateJoinedMessageRoomsUnsubscribe', value)
  },
  setUnsubscribeUpdateMessages({ commit }, value) {
    commit('setUpdateMessagesUnsubscribe', value)
  },
  async fetchGlobalDate({ commit }) {
    const isLocal =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'

    if (isLocal) {
      const date = new Date()
      commit('updateGlobalDate', date)
      return
    }

    try {
      const response = await fetch(
        // 'https://localhost:3000/'
        'https://spotwork-time-server-krwbx2bdoq-an.a.run.app/'
      )
      if (!response.ok) {
        throw new Error('Failed to fetch global time')
      }
      const data = await response.json()
      commit('updateGlobalDate', new Date(data.utc.timestamp))
    } catch (error) {
      console.error(error)
    }
  },
  updateHistories({ commit }, histories) {
    commit('setHistories', histories)
  }
}

function scheduleSignInLocationSaveOnce(user) {
  const uid = user?.uid
  if (!uid || signInLocationSaveInFlightUid === uid) {
    return
  }
  signInLocationSaveInFlightUid = uid
  queueMicrotask(() => {
    void saveLocation(user).finally(() => {
      if (signInLocationSaveInFlightUid === uid) {
        signInLocationSaveInFlightUid = null
      }
    })
  })
}

async function saveLocation(user) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(false)
      return
    }
    navigator.geolocation.getCurrentPosition(
      async (myPos) => {
        try {
          const latitude = myPos.coords.latitude
          const longitude = myPos.coords.longitude

          // 緯度と経度からメッシュコードを計算
          const meshCodes = calculateMeshCodes({
            lat: latitude,
            lng: longitude
          })

          const data = {
            userId: user.uid,
            lat: latitude,
            lng: longitude,
            mesh80km: meshCodes.mesh80km,
            mesh10km: meshCodes.mesh10km,
            mesh1km: meshCodes.mesh1km,
            mesh250m: meshCodes.mesh250m,
            createdAt: new Date(),
            type: 'signIn'
          }
          await database.locationCollection(user.uid).add(data)
          resolve(true)
        } catch (e) {
          console.error(e)
          resolve(false)
        }
      },
      (err) => {
        console.warn('getCurrentPosition failed:', err)
        resolve(false)
      },
      { enableHighAccuracy: false, maximumAge: 10000, timeout: 15000 }
    )
  })
}

/**
 * 通信の瞬断など一時的な失敗で「未登録」と誤判定しないよう、
 * Firestore読み取りを2〜3回リトライしてから諦める。
 */
async function retryAsync(fn, { retries = 3, delayMs = 500 } = {}) {
  let lastError
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, delayMs * attempt))
      }
    }
  }
  throw lastError
}

async function saveSignInTime(user, callback) {
  const userData = await retryAsync(async () => {
    const data = await database.userCollection().findById(user.uid)
    if (!data) {
      throw new Error('userData not found')
    }
    return data
  }).catch(() => null)
  if (!userData) {
    callback(false, false)
    return
  }

  // lastSignInTime の記録は付随的な処理であり、失敗しても登録状態の判定に影響させない
  retryAsync(() =>
    database.userCollection().update(user.uid, {
      lastSignInTime: new Date()
    })
  ).catch((error) => console.error(error))

  if (!userData.completeTutorial) {
    callback(false, false)
    return
  }

  const registCompleted = getIsRegistCompleted()
  if (registCompleted) {
    callback(true, true, true)
    return
  }

  const secret = await retryAsync(() =>
    database.secretCollection(user.uid).all()
  )

  let isSetLineId = false
  let isSetTelephoneNumber = false

  /** secretのidをキーにしてbodyを取得します
   * @type {(id: string)=>(string|undefined)}
   */
  const getSecretBodyById = (id) => {
    const target = secret.find((s) => s.id === id)
    return target ? target.body : undefined
  }

  if (getSecretBodyById('telephone_number')) {
    isSetTelephoneNumber = true
  }

  if (getSecretBodyById('line_id')) {
    isSetLineId = true
  }

  if (isSetTelephoneNumber && isSetLineId) {
    setIsRegistCompleted(true)
  }

  callback(isSetTelephoneNumber, isSetLineId)
}

function setIsRegistCompleted(registCompleted) {
  localStorage.setItem('registCompleted', registCompleted)
}

function getIsRegistCompleted() {
  return localStorage.getItem('registCompleted') === 'true'
}

async function getAgentId(state) {
  const customToken = await auth.currentUser.getIdTokenResult(false)
  const claims = customToken.claims
  state.agentId = claims.spoconAgentId || null
}
